import { useEffect, useMemo, useState } from "react";
import TopNav from "../components/TopNav";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { BarChart3, Lock } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Cell } from "recharts";

const COLORS = { operator: "#00E5FF", engineer: "#FFB000", hunter: "#00FF41", leader: "#FF3B30" };
const AXIS_STYLE = { fontFamily: "JetBrains Mono", fontSize: 12 };
const TOOLTIP_STYLE = { background: "#121212", border: "1px solid #27272A", fontFamily: "JetBrains Mono", fontSize: 12, borderRadius: 2 };
const TOOLTIP_CURSOR = { fill: "rgba(0,229,255,0.08)" };
const BAR_RADIUS = [2, 2, 0, 0];

export default function Metrics() {
  const { user } = useAuth();
  const isLeader = user?.role === "leader" || user?.role === "org_admin" || user?.role === "admin";
  const [m, setM] = useState(null);
  const [squad, setSquad] = useState([]);
  const [squadErr, setSquadErr] = useState(null);

  useEffect(() => {
    api.get("/metrics/summary").then((r) => setM(r.data));
    if (isLeader) {
      api.get("/metrics/squad")
        .then((r) => setSquad(r.data))
        .catch((e) => setSquadErr(e?.response?.data?.detail || e.message));
    }
  }, [isLeader]);

  const kpis = useMemo(() => (m ? [
    { k: "Qualification", v: `${m.qualification_rate}%`, c: "var(--cyan)" },
    { k: "Time to Ready", v: `${m.time_to_mission_ready_days}d`, c: "var(--amber)" },
    { k: "Exercise Perf", v: `${m.exercise_performance}%`, c: "var(--green)" },
    { k: "IR Effectiveness", v: `${m.ir_effectiveness}%`, c: "var(--cyan)" },
    { k: "Retention", v: `${m.retention}%`, c: "var(--green)" },
  ] : []), [m]);

  if (!m) return <div className="min-h-screen bg-[var(--bg)]"><TopNav /><div className="max-w-7xl mx-auto px-6 py-10 text-[var(--text-2)]">Loading…</div></div>;

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <TopNav />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-2">
          <BarChart3 size={20} color="var(--cyan)" strokeWidth={1.5} />
          <div className="overline">// FORCE_READINESS</div>
        </div>
        <h1 className="font-display font-bold text-4xl uppercase mb-8" data-testid="metrics-title">Readiness Dashboard</h1>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {kpis.map((kpi) => (
            <div key={kpi.k} className="flat-card p-5" data-testid={`m-${kpi.k.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="overline">{kpi.k}</div>
              <div className="font-mono text-3xl mt-2" style={{ color: kpi.c }}>{kpi.v}</div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="flat-card p-6">
            <div className="overline">// READINESS_BY_TRACK</div>
            <h2 className="font-display font-bold text-2xl uppercase mt-1 mb-4">Track Readiness</h2>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={m.track_stats}>
                  <XAxis dataKey="track" stroke="#71717A" style={AXIS_STYLE} />
                  <YAxis stroke="#71717A" style={AXIS_STYLE} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} cursor={TOOLTIP_CURSOR} />
                  <Bar dataKey="readiness" radius={BAR_RADIUS}>
                    {m.track_stats.map((t) => (
                      <Cell key={t.track} fill={COLORS[t.track] || "#00E5FF"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="flat-card p-6" data-testid="squad-roster-card">
            <div className="flex items-center justify-between">
              <div>
                <div className="overline">// FORCE_COMPOSITION</div>
                <h2 className="font-display font-bold text-2xl uppercase mt-1 mb-4">Squad Roster</h2>
              </div>
              {!isLeader && <Lock size={18} strokeWidth={1.5} color="var(--amber)" />}
            </div>

            {!isLeader ? (
              <div className="border border-[var(--amber)] p-5 bg-[var(--surface-2)]" data-testid="squad-locked">
                <div className="flex items-center gap-2 mb-2">
                  <Lock size={14} color="var(--amber)" />
                  <span className="font-mono text-xs tracking-widest uppercase text-[var(--amber)]">// CLASSIFIED // LEADER ACCESS REQUIRED</span>
                </div>
                <p className="text-sm text-[var(--text-2)]">
                  Force composition and individual trainee progress are restricted to <span className="text-white">Leader</span> and <span className="text-white">Admin</span> roles.
                  Your current role: <span className="font-mono text-[var(--cyan)]">{user?.role || "unknown"}</span>.
                </p>
                <p className="text-xs text-[var(--text-2)] mt-3 font-mono">
                  Contact your chain of command to escalate privileges.
                </p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[280px] overflow-auto">
                {squadErr && (
                  <div className="font-mono text-xs text-[var(--red)] border border-[var(--red)] p-2" data-testid="squad-error">{squadErr}</div>
                )}
                {squad.map((s) => (
                  <div key={s.id} className="border border-[var(--border)] p-3 flex items-center justify-between" data-testid={`squad-${s.email}`}>
                    <div>
                      <div className="font-display font-bold uppercase">{s.name}</div>
                      <div className="font-mono text-xs text-[var(--text-2)]">{s.email} &middot; {s.track}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-lg text-[var(--cyan)]">{s.avg_progress}%</div>
                      <div className="font-mono text-xs text-[var(--text-2)]">{s.certs} certs &middot; {s.courses} courses</div>
                    </div>
                  </div>
                ))}
                {squad.length === 0 && !squadErr && <div className="text-[var(--text-2)] text-sm">No trainees enrolled yet.</div>}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
